import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { AIImageModal } from "@/components/magazine/AIImageModal";

export const MediaPicker = ({ open, onClose, onSelect }) => {
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/mag/media").then((r) => setMedia(r.data.media || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files || [])) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/mag/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [r.data, ...m]);
      } catch (e) { toast.error(e?.response?.data?.detail || `${f.name} failed`); }
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="mag-media-picker">
      <div className="bg-bg-surface border border-line rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="font-display font-bold">Media Library</div>
          <div className="flex gap-2">
            <button onClick={() => setAiOpen(true)} data-testid="mag-media-generate-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/60 text-brand-amber px-3 py-1.5 text-xs font-bold">
              <Sparkles className="h-3.5 w-3.5" /> Generate
            </button>
            <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="mag-media-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-3 py-1.5 text-xs font-bold">
              <UploadCloud className="h-3.5 w-3.5" /> {busy ? "Uploading…" : "Upload"}
            </button>
            <button onClick={onClose} data-testid="mag-media-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
          </div>
          <input ref={inputRef} type="file" multiple accept="image/*,audio/*,video/mp4" className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 p-4 overflow-y-auto">
          {media.map((m) => (
            <button key={m.media_id} onClick={() => { onSelect(m); onClose(); }}
              data-testid={`mag-media-item-${m.media_id}`}
              className="group rounded-lg border border-line overflow-hidden hover:border-brand-amber transition text-left">
              {m.type === "image" ? (
                <img src={m.file_url} alt={m.alt_text || m.filename} className="w-full h-24 object-cover" />
              ) : (
                <div className="w-full h-24 flex items-center justify-center bg-bg-elevated text-xs font-mono text-ink-muted">{m.type}</div>
              )}
              <div className="px-2 py-1 text-[10px] font-mono text-ink-muted truncate">{m.filename}</div>
            </button>
          ))}
          {!media.length && <div className="col-span-4 text-center text-sm text-ink-muted py-8">No media yet — upload something.</div>}
        </div>
      </div>
      <AIImageModal open={aiOpen} onClose={() => setAiOpen(false)}
        onUse={(m) => { setMedia((prev) => [m, ...prev]); onSelect(m); onClose(); }} />
    </div>
  );
};
